import DocumentStore from 'notebook/documents/DocumentStore';
import {Table} from 'notebook/tables/constants/TableConstants';
import {CellGrids, computeCellGridCost, computeConstraints} from 'notebook/tables/excess/CostAnalyzer';
import Parser from 'notebook/tables/excess/Parser';
import TableUtil from 'notebook/tables/TableUtil';
import {AnyStructuredTable, Note, NoteLinkedObjectType} from 'notebook/util/NoteUtilConstants';

export interface TablePerformanceSummary {
  tableId: string;
  documentName: string | null;
  name: string;
  numCells: number;
  numRows: number;
  numColumns: number;
  formulaCost: number;
  costBreakdown: {[key: string]: number};
}

export interface NotePerformanceSummary {
  numDocuments: number;
  numTables: number;
  numCells: number;
  totalFormulaCost: number;
  tableSummaries: Array<TablePerformanceSummary>;
}

export type FormulaCostTier = 'over_limit' | 'nearing_limit' | 'good';
export const TOTAL_FORMULA_COST_LIMIT = 165_000;
export const TOTAL_FORMULA_COST_LIMIT_WARNING = TOTAL_FORMULA_COST_LIMIT * 0.5;

export const TABLE_FORMULA_COST_LIMIT = 30_000;
export const TABLE_FORMULA_COST_LIMIT_WARNING = TABLE_FORMULA_COST_LIMIT * 0.5;

function _buildCellGrids(tablesByDocumentName: {
  [documentName: string]: Array<Table | AnyStructuredTable>;
}): CellGrids {
  const cellGrids: CellGrids = {};
  for (const [documentName, tables] of Object.entries(tablesByDocumentName)) {
    cellGrids[documentName] = {};
    for (const table of tables) {
      if ('type' in table && table.type === NoteLinkedObjectType.STRUCTURED_TABLE) {
        cellGrids[documentName][table.name] = table.rows.map((row) => row.cells);
      } else {
        cellGrids[documentName][table.name] = (table as Table).table;
      }
    }
  }
  return cellGrids;
}

export const _forTesting = {
  getPlainTablePerformanceSummary,
  getStructuredTablePerformanceSummary,
  buildCellGrids: _buildCellGrids,
};

function getPlainTablePerformanceSummary(
  table: Table,
  documentName: string | null,
  formulaCost: number,
  costBreakdown: {[key: string]: number}
): TablePerformanceSummary {
  const numRows = table.table.length;
  const numColumns = table.table[0].length;
  const numCells = numRows * numColumns;

  return {
    tableId: `${documentName}-${table.name}`,
    documentName,
    name: table.name,
    numCells,
    numRows,
    numColumns,
    formulaCost,
    costBreakdown,
  };
}

function getStructuredTablePerformanceSummary(
  table: AnyStructuredTable,
  documentName: string | null,
  formulaCost: number,
  costBreakdown: {[key: string]: number}
): TablePerformanceSummary {
  const numRows = table.rows.length;
  const numColumns = table.columns.length;
  const numCells = numRows * numColumns;
  return {
    tableId: `${documentName}-${table.name}`,
    documentName,
    name: table.name,
    numCells,
    numRows,
    numColumns,
    formulaCost,
    costBreakdown,
  };
}

function formulaCostIsOverMaximum(formulaCost: number): boolean {
  return formulaCost >= TOTAL_FORMULA_COST_LIMIT;
}

function formulaCostIsInWarningRange(formulaCost: number): boolean {
  return formulaCost >= TOTAL_FORMULA_COST_LIMIT_WARNING && formulaCost < TOTAL_FORMULA_COST_LIMIT;
}

function tableFormulaCostIsOverMaximum(formulaCost: number): boolean {
  return formulaCost >= TABLE_FORMULA_COST_LIMIT;
}

function tableFormulaCostIsOverWarningThreshold(formulaCost: number): boolean {
  return formulaCost >= TABLE_FORMULA_COST_LIMIT_WARNING;
}

export const TableLimitUtils = {
  formulaCostIsOverMaximum,
  formulaCostIsInWarningRange,

  getFormulaCostTier(totalFormulaCost: number): FormulaCostTier {
    return formulaCostIsOverMaximum(totalFormulaCost)
      ? 'over_limit'
      : formulaCostIsInWarningRange(totalFormulaCost)
        ? 'nearing_limit'
        : 'good';
  },

  getFormulaCostTierForTable(tableFormulaCost: number): FormulaCostTier {
    return tableFormulaCostIsOverMaximum(tableFormulaCost)
      ? 'over_limit'
      : tableFormulaCostIsOverWarningThreshold(tableFormulaCost)
        ? 'nearing_limit'
        : 'good';
  },

  analyzeNotePerformance(note: Note): NotePerformanceSummary {
    const tables = TableUtil.getTablesFromNote(note);
    return this.analyzePerformanceOfTables({entry: tables});
  },

  analyzePerformanceOfTables(tablesByDocumentName: {
    [documentName: string]: Array<Table | AnyStructuredTable>;
  }): NotePerformanceSummary {
    let numCells = 0;
    let numTables = 0;
    let totalFormulaCost = 0;
    const numDocuments = Object.keys(tablesByDocumentName).length;
    const cellGrids = _buildCellGrids(tablesByDocumentName);
    const constraints = computeConstraints(cellGrids);
    const tableSummaries: Array<TablePerformanceSummary> = [];

    for (const [documentName, tables] of Object.entries(tablesByDocumentName)) {
      for (const table of tables) {
        const cellGrid = cellGrids[documentName][table.name];
        const {totalCost: formulaCost, costBreakdown} = computeCellGridCost(
          cellGrid,
          constraints,
          documentName,
          table.name,
          Parser.parse
        );
        const summary =
          'type' in table && table.type === NoteLinkedObjectType.STRUCTURED_TABLE
            ? getStructuredTablePerformanceSummary(table, documentName, formulaCost, costBreakdown)
            : getPlainTablePerformanceSummary(table as Table, documentName, formulaCost, costBreakdown);
        tableSummaries.push(summary);
        numTables++;
        numCells += summary.numCells;
        totalFormulaCost += summary.formulaCost;
      }
    }

    return {
      numDocuments,
      numTables,
      numCells,
      totalFormulaCost,
      tableSummaries,
    };
  },
};

export function getTablesByDocumentNameFromDocumentIds(documentIds: Array<string>): {
  [documentName: string]: Array<Table | AnyStructuredTable>;
} {
  const tablesByDocumentName: {[documentName: string]: Array<Table | AnyStructuredTable>} = {};
  for (const documentId of documentIds) {
    const document = DocumentStore.getDocument(documentId);
    if (document) {
      tablesByDocumentName[document.name] = TableUtil.getTablesFromNote(
        document.documentVersion.entryVersion.note
      );
    }
  }
  return tablesByDocumentName;
}
